import { createContext, useContext } from "react";
import { DiscountType } from "@/types/discount";
import type {
  Control,
  FieldErrors,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";

type FormContextType = {
  register: UseFormRegister<CheckoutInput>;
  errors: FieldErrors<CheckoutInput>;
  watch: UseFormWatch<CheckoutInput>;
  control: Control<CheckoutInput, any>;
  setValue: UseFormSetValue<CheckoutInput>;
  handleSubmit: UseFormHandleSubmit<CheckoutInput, undefined>;
} | null;

const FormContext = createContext<FormContextType>(null);

type PropsType = {
  children: React.ReactNode;
  value: Exclude<FormContextType, null>;
};

export function CheckoutFormProvider({ children, value }: PropsType) {
  return <FormContext.Provider value={value}>{children}</FormContext.Provider>;
}

export function useCheckoutForm() {
  const formContext = useContext(FormContext);

  if (!formContext) {
    throw new Error(
      "useCheckoutForm must be used within a CheckoutFormProvider",
    );
  }

  return formContext;
}

export type CheckoutInput = {
  billing: {
    customerName: string;
    companyName?: string;
    regionName: string;
    address: {
      street: string;
      houseNumber: string;
      apartmentNumber?: string;
      floor?: string;
    };
    town: string;
    zipCode?: string;
    deliveryZone?: string; // "City||ZoneName" — used to resolve per-zone delivery cost
    country?: string;
    phone: string;
    email: string;
    createAccount?: boolean;
    password?: string;
    confirmPassword?: string;
    isNextDayDelivery?: boolean;
    nextDayDeliveryDate?: string;
    wantsInvoice?: boolean;
    invoiceNip?: string;
  };
  shipToDifferentAddress: boolean;
  shipping: {
    countryName?: string;
    address: {
      street: string;
      houseNumber: string;
      apartmentNumber?: string;
      floor?: string;
    };
    town: string;
    zipCode?: string;
    deliveryZone?: string;
    country?: string;
    phone: string;
    email: string;
    isNextDayDelivery?: boolean;
    nextDayDeliveryDate?: string;
  };
  shippingMethod: {
    name: string;
    price: number;
  };
  paymentMethod: string;
  codPaymentType?: "card" | "cash";
  notes?: string;
  products: {
    id: string;
    price: number;
    quantity: number;
  }[];
  cartSubtotal?: number;
  couponCode?: string;
  couponDiscountValue?: number;
  couponDiscountType?: DiscountType;
  couponDiscountAmount?: number;
};
